import { useState } from "react";
import { Link } from "react-router-dom";
import AccountLayout from "../components/AccountLayout.jsx";
import PlanCard from "../components/billing/PlanCard.jsx";
import PriceTag from "../components/billing/PriceTag.jsx";
import StatusPill from "../components/billing/StatusPill.jsx";
import { useCoachClientPlans } from "../hooks/useCoachClientPlans.js";
import { useStripeConnect } from "../hooks/useStripeConnect.js";
import { PlanCardSkeletonGrid } from "../components/loading/SkeletonBlocks.jsx";
import { formatMoneyFromMinorUnits } from "../lib/planCatalog.js";

const EMPTY_FORM = {
  name: "",
  description: "",
  billingType: "recurring",
  amountDollars: "",
  interval: "month",
  termWeeks: "12",
};

function planTypeLabel(plan) {
  if (plan.billingType === "one_time") {
    return `${plan.termWeeks}-week package`;
  }
  return plan.interval === "year" ? "Yearly" : "Monthly";
}

function planTypePillVariant(plan) {
  if (plan.billingType === "one_time") return "warning";
  if (plan.interval === "year") return "success";
  return "primary";
}

function PlanGrid({ plans, mutating, onArchive }) {
  if (plans.length === 0) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {plans.map((plan) => (
        <PlanCard
          key={plan.id}
          tone="shell"
          title={plan.name}
          badge={
            <StatusPill variant={planTypePillVariant(plan)} size="sm">
              {planTypeLabel(plan)}
            </StatusPill>
          }
          footer={
            plan.status === "active" ? (
              <button
                type="button"
                disabled={mutating}
                onClick={() => onArchive(plan.id)}
                className="mt-4 rounded-[10px] border border-hc-surface-border-strong bg-hc-page-shell px-3 py-1.5 text-xs font-medium text-hc-text-muted transition hover:bg-hc-error/10 hover:border-hc-error/30 hover:text-hc-error disabled:opacity-50"
              >
                Archive plan
              </button>
            ) : null
          }
        >
          <PriceTag
            amount={formatMoneyFromMinorUnits(plan.amountCents, plan.currency)}
            suffix={
              plan.billingType === "recurring"
                ? plan.interval === "year"
                  ? "/yr"
                  : "/mo"
                : " one-time"
            }
          />
          {plan.description && (
            <p className="mt-2 text-xs text-hc-text-dim">{plan.description}</p>
          )}
        </PlanCard>
      ))}
    </div>
  );
}

export default function CoachClientPlansPage() {
  const stripeConnect = useStripeConnect();
  const { plans, isLoading, error, mutating, createPlan, archivePlan } = useCoachClientPlans({
    includeArchived: false,
  });

  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const recurringPlans = plans.filter((p) => p.billingType === "recurring");
  const oneTimePlans = plans.filter((p) => p.billingType === "one_time");
  const canCreate = stripeConnect.connect?.ready;

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    const amountCents = Math.round(Number(form.amountDollars) * 100);
    if (!form.name.trim()) {
      setFormError("Plan name is required.");
      return;
    }
    if (!Number.isFinite(amountCents) || amountCents < 50) {
      setFormError("Enter a valid price (minimum $0.50 CAD, before tax).");
      return;
    }
    try {
      await createPlan({
        name: form.name.trim(),
        description: form.description.trim(),
        billingType: form.billingType,
        amountCents,
        interval: form.billingType === "recurring" ? form.interval : undefined,
        termWeeks: form.billingType === "one_time" ? Number(form.termWeeks) : undefined,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
    } catch (err) {
      setFormError(err?.message ?? String(err));
    }
  }

  async function handleArchive(planId) {
    if (!window.confirm("Archive this plan? Clients with active purchases cannot be archived.")) {
      return;
    }
    try {
      await archivePlan(planId);
    } catch {
      /* error surfaced via hook */
    }
  }

  function openCreateForm() {
    setShowForm(true);
    setFormError(null);
  }

  return (
    <AccountLayout>
      <div className="mb-2">
        <Link to="/billing" className="text-xs text-hc-text-dim hover:text-hc-text transition">
          ← Back to billing
        </Link>
      </div>

      <section className="rounded-2xl border border-hc-primary/20 bg-hc-primary/5 p-5">
        <h2 className="text-sm font-semibold text-hc-text">How client billing works</h2>
        <ul className="mt-3 space-y-2 text-xs text-hc-text-dim">
          <li className="flex gap-2">
            <span className="text-hc-primary">1.</span>
            Create recurring subscriptions or fixed-term packages for your roster clients.
          </li>
          <li className="flex gap-2">
            <span className="text-hc-primary">2.</span>
            Clients subscribe on their billing page — plans unlock program access.
          </li>
          <li className="flex gap-2">
            <span className="text-hc-primary">3.</span>
            You set prices in CAD; sales tax is added for clients at checkout.
          </li>
        </ul>
      </section>

      {!canCreate && !stripeConnect.isLoading && (
        <section className="mt-4 rounded-2xl border border-hc-warning/30 bg-hc-warning-muted px-5 py-4">
          <p className="text-sm font-medium text-hc-text">Payout setup required</p>
          <p className="mt-1 text-xs text-hc-text-dim">
            Complete payout setup on{" "}
            <Link to="/billing" className="text-hc-text-muted hover:text-hc-text transition">
              billing
            </Link>{" "}
            before creating client plans.
          </p>
        </section>
      )}

      <section className="mt-4 rounded-2xl border border-hc-surface-border bg-hc-surface p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">
              Your plans
            </h2>
            <p className="mt-1 text-xs text-hc-text-dim">
              Recurring subscriptions and fixed-term packages for roster clients.
            </p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => (showForm ? setShowForm(false) : openCreateForm())}
              className="rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            >
              {showForm ? "Cancel" : "New plan"}
            </button>
          )}
        </div>

        {showForm && canCreate && (
          <form
            onSubmit={handleSubmit}
            className="mb-6 space-y-4 rounded-[14px] border border-hc-surface-border bg-hc-page-shell p-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-xs text-hc-text-dim">
                Plan name
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-[10px] border border-hc-surface-border bg-hc-surface px-3 py-2 text-sm text-hc-text"
                  placeholder="Monthly coaching"
                />
              </label>
              <label className="block text-xs text-hc-text-dim">
                Price (CAD, before tax)
                <input
                  type="number"
                  min="0.5"
                  step="0.01"
                  value={form.amountDollars}
                  onChange={(e) => setForm({ ...form, amountDollars: e.target.value })}
                  className="mt-1 w-full rounded-[10px] border border-hc-surface-border bg-hc-surface px-3 py-2 text-sm text-hc-text"
                  placeholder="200.00"
                />
              </label>
            </div>
            <label className="block text-xs text-hc-text-dim">
              Description (optional)
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="mt-1 w-full rounded-[10px] border border-hc-surface-border bg-hc-surface px-3 py-2 text-sm text-hc-text"
              />
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-hc-text">
                <input
                  type="radio"
                  checked={form.billingType === "recurring"}
                  onChange={() => setForm({ ...form, billingType: "recurring" })}
                />
                Recurring
              </label>
              <label className="flex items-center gap-2 text-sm text-hc-text">
                <input
                  type="radio"
                  checked={form.billingType === "one_time"}
                  onChange={() => setForm({ ...form, billingType: "one_time" })}
                />
                One-time package
              </label>
            </div>
            {form.billingType === "recurring" ? (
              <div className="flex gap-3">
                {["month", "year"].map((iv) => (
                  <label key={iv} className="flex items-center gap-2 text-sm text-hc-text">
                    <input
                      type="radio"
                      checked={form.interval === iv}
                      onChange={() => setForm({ ...form, interval: iv })}
                    />
                    {iv === "month" ? "Monthly" : "Yearly"}
                  </label>
                ))}
              </div>
            ) : (
              <label className="block text-xs text-hc-text-dim">
                How long is the package available for? (weeks)
                <input
                  type="number"
                  min="1"
                  value={form.termWeeks}
                  onChange={(e) => setForm({ ...form, termWeeks: e.target.value })}
                  className="mt-1 ml-1 w-24 rounded-[10px] border border-hc-surface-border bg-hc-surface px-3 py-2 text-sm text-hc-text"
                />
              </label>
            )}
            <p className="text-xs text-hc-text-dim">
              Clients pay this amount plus applicable tax at checkout.
            </p>
            {formError && <p className="text-xs text-hc-error">{formError}</p>}
            <button
              type="submit"
              disabled={mutating}
              className="rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {mutating ? "Creating…" : "Create plan"}
            </button>
          </form>
        )}

        {error && <p className="mb-3 text-xs text-hc-error break-words">{error}</p>}

        {isLoading ? (
          <PlanCardSkeletonGrid count={2} />
        ) : plans.length === 0 && canCreate ? (
          <div className="rounded-[14px] border border-dashed border-hc-surface-border-strong px-6 py-12 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-hc-surface-border bg-hc-page-shell">
              <i className="icon-credit-card text-lg leading-none text-hc-text-dim" aria-hidden="true" />
            </div>
            <p className="mt-4 text-sm font-medium text-hc-text">No client plans yet</p>
            <p className="mx-auto mt-1.5 max-w-sm text-xs text-hc-text-dim">
              Create a subscription or package so roster clients can subscribe and unlock your
              programs.
            </p>
            {!showForm && (
              <button
                type="button"
                onClick={openCreateForm}
                className="mt-5 rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Create your first plan
              </button>
            )}
          </div>
        ) : plans.length === 0 ? null : (
          <div className="space-y-6">
            {recurringPlans.length > 0 && (
              <div>
                <p className="mb-3 text-xs font-medium text-hc-text-muted">Subscriptions</p>
                <PlanGrid plans={recurringPlans} mutating={mutating} onArchive={handleArchive} />
              </div>
            )}
            {oneTimePlans.length > 0 && (
              <div>
                <p className="mb-3 text-xs font-medium text-hc-text-muted">Packages</p>
                <PlanGrid plans={oneTimePlans} mutating={mutating} onArchive={handleArchive} />
              </div>
            )}
          </div>
        )}
      </section>
    </AccountLayout>
  );
}
